import { useMemo, useState, useEffect } from "react";
import { getLocParameterFromURL } from "Utils/tourNavigation";
import { CustomTrail } from "../../types/tour";

/**
 * Hook for URL parsing and validation
 * Optimized to prevent memory leaks and unnecessary recalculations
 */
export const useURLParser = () => {
  // Track URL changes manually to prevent constant recalculation
  const [currentUrl, setCurrentUrl] = useState(() => window.location.href);

  // Listen for URL changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentUrl(window.location.href);
    };

    // Listen for both popstate and hashchange
    window.addEventListener("popstate", handleLocationChange);
    window.addEventListener("hashchange", handleLocationChange);

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
      window.removeEventListener("hashchange", handleLocationChange);
    };
  }, []);

  // Parse current location parameter - only when URL actually changes
  const getCurrentLocFromURL = useMemo(() => {
    return getLocParameterFromURL(currentUrl);
  }, [currentUrl]);

  // Validate point index function - stable reference
  const getValidatedPointIndex = useMemo(
    () => (trail: CustomTrail | null, urlPointIndex: number): number => {
      if (
        trail?.Stops &&
        !Number.isNaN(urlPointIndex) &&
        urlPointIndex >= 0 &&
        urlPointIndex < trail.Stops.length
      ) {
        return urlPointIndex;
      }
      return 0;
    },
    []
  );

  return {
    getCurrentLocFromURL,
    getValidatedPointIndex,
  };
};
